import { CustomerIcon } from '@/assets/icons/customer-icon';
import { DatePicker } from '@/components/ui/date-picker';
import { LinkText } from '@/components/ui/link-text';
import TimePicker from '@/components/ui/time-picker';
import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { Dialog } from '@/features/dialog/dialog';
import { DialogForm } from '@/features/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import type { ModalInnerProps } from '@/features/modal';
import { type RichTextValue } from '@/features/rich-text/components/rich-text-input';
import type { AIActionType } from '@/features/rich-text/hooks/use-improve-writing';
import { CustomerSelect } from '@/features/shared/components/form-items-with-fetch/customer-select';
import { useVoiceRecording } from '@/lib/contexts/voice-recording-context';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CustomerDTO } from '@/server/dto/customer-dto';
import type { RouterInput } from '@/server/types/infer-trpc-types';
import { api } from '@/utils/api';
import type { CrmNoteType } from '@prisma/client';
import { format, formatISO, isToday, min, roundToNearestMinutes } from 'date-fns';
import { CalendarIcon, ClockIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { NoteFormFields } from './note-form-fields';
import { NoteTypeSelector } from './note-type-selector';

export interface CreateNoteFormProps {
    customer?: CustomerDTO;
    title?: RichTextValue;
    onCancel?: () => void;
    onSuccess?: () => void;
}

const getMaxDate = () => roundToNearestMinutes(new Date(), { roundingMethod: 'floor', nearestTo: 15 });

export const CreateNoteForm = ({ customer, title, onSuccess, onCancel }: CreateNoteFormProps) => {
    const { t } = useLocale();
    const { toast } = useToast();
    const utils = api.useUtils();
    const { currentSessionId } = useVoiceRecording();

    const sendEvent = useAnalytics();
    const [isAIBusy, setIsAIBusy] = useState<boolean>(false);
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);

    const handleAIInteraction = (action: AIActionType, sessionId: string) => {
        switch (action) {
            case 'accept':
                // Store the session ID for any AI interaction (we'll use it when the note is created)
                setAISessionId(sessionId);
                setIsAIBusy(false);
                break;
            case 'request':
            case 'regenerate':
                setIsAIBusy(true);
                break;
            case 'reject':
                setIsAIBusy(false);
                break;
            default:
                // no-op for unknown actions
                break;
        }
    };

    const [noteType, setNoteType] = useState<CrmNoteType>('EVENT');

    const defaultValues = {
        title,
        customerId: customer?.customerId,
        businessCreatedAt: getMaxDate().toISOString(),
    };

    const {
        control,
        handleSubmit,
        clearErrors,
        formState: { errors: formErrors },
    } = useForm<Omit<RouterInput['platoUser']['command']['notes']['create'], 'noteType'>>({
        defaultValues,
    });

    useEffect(() => {
        clearErrors('title');
    }, [noteType, clearErrors]);

    const getSuccessToastMessage = (noteType: CrmNoteType) => {
        switch (noteType) {
            case 'EVENT':
                return {
                    title: t('notes__form_create_toast__success_title__event', { ns: 'notes' }),
                    description: t('notes__form_create_toast__success_description__event', { ns: 'notes' }),
                };
            case 'PHONE_CALL':
                return {
                    title: t('notes__form_create_toast__success_title__phone_call', { ns: 'notes' }),
                    description: t('notes__form_create_toast__success_description__phone_call', {
                        ns: 'notes',
                    }),
                };
            default:
                return {
                    title: t('notes__form_create_toast__success_title', { ns: 'notes' }),
                    description: t('notes__form_create_toast__success_description', { ns: 'notes' }),
                };
        }
    };

    const createMutation = api.platoUser.command.notes.create.useMutation({
        onSuccess: async (note) => {
            // Invalidate activity feed queries to refresh the data
            utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();

            const toastMessage = getSuccessToastMessage(noteType);

            toast({
                icon: 'success',
                variant: 'default',
                title: toastMessage.title,
                description: toastMessage.description,
                createdLink: <LinkText href={`/customers/${note.customerId}/activity`} />,
            });

            sendEvent('customer_interaction_created', {
                note_id: note.noteId,
                customer_id: note.customerId ?? 'unknown',
                note_type: noteType,
                ai_session_id: aiSessionId,
            });

            onSuccess?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const onSubmit = handleSubmit((data) => {
        createMutation.mutate({
            ...data,
            businessCreatedAt: noteType === 'NOTE' ? undefined : data.businessCreatedAt,
            noteType,
        });
    });

    return (
        <DialogForm className="sm:max-w-2xl">
            <DialogForm.Header title={t('notes__form_create_title', { ns: 'notes' })} />
            <DialogForm.Content>
                <NoteTypeSelector value={noteType} onChange={setNoteType} />
                <NoteFormFields
                    noteType={noteType}
                    control={control}
                    errors={formErrors}
                    onAIInteraction={handleAIInteraction}
                />
                <DialogForm.ItemList grid>
                    {/* Customer Field */}
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label htmlFor="customerId" icon={CustomerIcon} required>
                            {t('notes__form__customer_label', { ns: 'notes' })}
                        </DialogForm.Label>
                        <DialogForm.Body className="flex flex-col gap-1">
                            <Controller
                                name="customerId"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('form__customer_required', { ns: 'common' }),
                                    },
                                }}
                                render={({ field: { value, onChange } }) => (
                                    <CustomerSelect
                                        customer={customer}
                                        value={value}
                                        onChange={onChange}
                                        className="w-full"
                                        variant="inline"
                                        error={!!formErrors.customerId}
                                    />
                                )}
                            />
                            <DialogForm.Error error={formErrors.customerId} />
                        </DialogForm.Body>
                    </DialogForm.Item>

                    {/* Date and Time for EVENT and PHONE_CALL */}
                    {(noteType === 'EVENT' || noteType === 'PHONE_CALL') && (
                        <>
                            {/* Date Field */}
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label htmlFor="businessDate" icon={CalendarIcon} required>
                                    {t('notes__form__date_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex flex-col gap-1">
                                    <Controller
                                        name="businessCreatedAt"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                disableIcon
                                                mode="single"
                                                value={field.value ? new Date(field.value) : new Date()}
                                                valuePrefix=""
                                                onChange={(value) => {
                                                    if (!value) return;

                                                    const baseDate = field.value
                                                        ? new Date(field.value)
                                                        : new Date();

                                                    const timePart = formatISO(baseDate, {
                                                        representation: 'time',
                                                    });

                                                    const datePart = formatISO(value, {
                                                        representation: 'date',
                                                    });

                                                    const nextDate = new Date(datePart + 'T' + timePart);

                                                    field.onChange(
                                                        min([getMaxDate(), nextDate]).toISOString(),
                                                    );
                                                }}
                                                disabled={{ after: new Date() }}
                                                className="w-full"
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>

                            {/* Time Field */}
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label htmlFor="businessTime" icon={ClockIcon} required>
                                    {t('notes__form__time_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex flex-col gap-1">
                                    <Controller
                                        name="businessCreatedAt"
                                        control={control}
                                        render={({ field }) => (
                                            <TimePicker
                                                value={
                                                    field.value
                                                        ? format(new Date(field.value), 'HH:mm:ss')
                                                        : null
                                                }
                                                onChange={(value) => {
                                                    const baseDate = field.value
                                                        ? new Date(field.value)
                                                        : new Date();

                                                    const datePart = formatISO(baseDate, {
                                                        representation: 'date',
                                                    });

                                                    const nextDate = new Date(datePart + 'T' + value);

                                                    field.onChange(nextDate.toISOString());
                                                }}
                                                max={
                                                    !field.value || isToday(field.value)
                                                        ? format(getMaxDate(), 'HH:mm:ss')
                                                        : undefined
                                                }
                                                placeholder={t('time_picker__placeholder', { ns: 'common' })}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </>
                    )}
                </DialogForm.ItemList>
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                onCancel={onCancel}
                onSubmit={onSubmit}
                disabled={createMutation.isLoading || isAIBusy || !!currentSessionId}
            />
        </DialogForm>
    );
};

type NoteFormDialogProps = ModalInnerProps & CreateNoteFormProps;

export const NoteFormDialog = DialogStateManager.create<NoteFormDialogProps>(({ onSuccess, ...props }) => {
    const { visible, remove } = useDialog();

    return (
        <Dialog open={visible} showClose onClose={remove}>
            <CreateNoteForm
                onCancel={remove}
                onSuccess={() => {
                    remove();
                    onSuccess?.();
                }}
                {...props}
            />
        </Dialog>
    );
});
